class CustomBlogDirectory extends HTMLElement {
  connectedCallback() {
    if (this.eventController) return;
    if (!this.initialized) this.initialize();
    this.eventController = new AbortController();
    this.bindEvents();
    this.update();
    if (!this.ready) this.loadAllPages();
  }

  disconnectedCallback() {
    this.eventController?.abort();
    this.eventController = null;
    this.loadingController?.abort();
    this.loadingController = null;
  }

  initialize() {
    this.grid = this.querySelector('[data-blog-grid]');
    this.cards = Array.from(this.querySelectorAll('[data-blog-card]'));
    this.count = this.querySelector('[data-article-count]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.loadMoreButton = this.querySelector('[data-load-more]');
    this.categoryList = this.querySelector('[data-category-list]');
    this.topicList = this.querySelector('[data-topic-list]');
    this.categorySelects = Array.from(this.querySelectorAll('[data-category-select]'));
    this.animalSelects = Array.from(this.querySelectorAll('[data-animal-select]'));
    this.topicSelects = Array.from(this.querySelectorAll('[data-topic-select]'));
    this.sortSelects = Array.from(this.querySelectorAll('[data-blog-sort]'));
    this.initialCount = Number(this.dataset.initialCount) || 6;
    this.loadCount = Number(this.dataset.loadCount) || 6;
    this.topicMode = this.dataset.topicMode === 'manual' ? 'manual' : 'automatic';
    this.topicLimit = Number(this.dataset.topicLimit) || 6;
    this.manualTopicKeys = (this.dataset.manualTopics || '')
      .split('||')
      .map((tag) => this.normalize(tag))
      .filter(Boolean);
    this.animalTagKeys = (this.dataset.animalTags || '')
      .split(',')
      .map((tag) => this.normalize(tag))
      .filter(Boolean);
    this.visibleCount = this.initialCount;
    this.activeCategory = '';
    this.activeAnimal = '';
    this.activeTopic = '';
    this.sortOrder = 'newest';
    this.status = this.querySelector('[data-loading-status]');
    this.statusMessage = this.querySelector('[data-loading-message]');
    this.retryButton = this.querySelector('[data-retry]');
    this.pagination = this.querySelector('[data-pagination]');
    this.countContainer = this.count?.closest('.custom-blog-directory__count');
    this.initialCards = [...this.cards];
    this.totalPages = Number(this.dataset.totalPages);
    this.totalArticles = Number(this.dataset.totalArticles);
    this.currentPage = Number(this.dataset.currentPage);
    this.ready = this.totalPages <= 1 && this.cards.length === this.totalArticles;
    this.initialized = true;

    this.buildFilters();
  }

  listen(target, type, handler) {
    target?.addEventListener(type, (event) => {
      if (this.ready || target === this.retryButton) handler(event);
    }, { signal: this.eventController.signal });
  }

  async loadAllPages() {
    if (this.loadingController || this.ready) return;
    const controller = new AbortController();
    this.loadingController = controller;
    this.status.hidden = false;
    this.statusMessage.textContent = 'Loading all articles…';
    this.retryButton.hidden = true;
    this.grid?.setAttribute('aria-busy', 'true');
    this.update();

    try {
      if (!Number.isInteger(this.totalPages) || this.totalPages < 1 ||
          !Number.isInteger(this.currentPage) || this.currentPage < 1 || this.currentPage > this.totalPages ||
          !Number.isInteger(this.totalArticles) || this.totalArticles < 0) {
        throw new Error('Invalid article pagination metadata');
      }
      // Stage cards off-DOM: retries never expose a partially filtered/sorted archive.
      const cardsById = new Map();
      const addCards = (cards) => cards.forEach((card) => {
        const id = card.dataset.articleId;
        if (!id) throw new Error('Missing article ID');
        if (!cardsById.has(id)) cardsById.set(id, card);
      });
      addCards(this.initialCards);

      for (let page = 1; page <= this.totalPages; page++) {
        if (page === this.currentPage) continue;
        // Keep the locale, any native tagged-blog route, and theme preview context.
        const url = new URL(window.location.pathname, window.location.origin);
        url.search = window.location.search;
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(this.dataset.pageParam || 'page', String(page));
        const timeout = setTimeout(() => controller.abort(), 20000);
        let html;
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`Article request failed: ${response.status}`);
          html = await response.text();
        } finally {
          clearTimeout(timeout);
        }
        if (this.loadingController !== controller || !this.isConnected) return;
        const document = new DOMParser().parseFromString(html, 'text/html');
        const directory = Array.from(document.querySelectorAll('custom-blog-directory'))
          .find((element) => element.dataset.sectionId === this.dataset.sectionId);
        if (!directory || Number(directory.dataset.currentPage) !== page ||
            Number(directory.dataset.totalPages) !== this.totalPages ||
            Number(directory.dataset.totalArticles) !== this.totalArticles) {
          throw new Error('Article pagination changed or section response is invalid');
        }
        const cards = Array.from(directory.querySelectorAll('[data-blog-card]'));
        if (!cards.length) throw new Error('Article page is empty');
        cards.forEach((card) => {
          card.hidden = true;
          card.querySelectorAll('img').forEach((image) => { image.loading = 'lazy'; });
        });
        addCards(cards);
      }

      if (cardsById.size !== this.totalArticles) throw new Error('Article archive is incomplete');
      if (this.loadingController !== controller || !this.isConnected) return;
      this.cards = Array.from(cardsById.values());
      this.grid.replaceChildren(...this.cards);
      this.ready = true;
      this.buildFilters();
      if (this.pagination) this.pagination.hidden = true;
      this.statusMessage.textContent = 'All articles loaded.';
      this.status.hidden = true;
    } catch (error) {
      if (this.loadingController !== controller || !this.isConnected) return;
      this.statusMessage.textContent = 'Could not load all articles. Retry, use the article page links, or reload this page if the articles have changed.';
      this.retryButton.hidden = false;
    } finally {
      if (this.loadingController === controller) {
        this.loadingController = null;
        this.grid?.setAttribute('aria-busy', 'false');
        this.update();
      }
    }
  }

  normalize(value) {
    return value.trim().toLocaleLowerCase();
  }

  getCardTopics(card) {
    return (card.dataset.tags || '')
      .split('||')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  getCardCategories(card) {
    const values = (card.dataset.categories || '').split('||');
    const labels = (card.dataset.categoryLabels || '').split('||');

    return values
      .map((value, index) => ({ value: value.trim(), label: (labels[index] || '').trim() }))
      .filter((category) => category.value && category.label);
  }

  buildFilters() {
    this.categoryList?.querySelectorAll('[data-category-filter]').forEach((button) => {
      if (button.dataset.categoryFilter) button.remove();
    });
    this.topicList?.replaceChildren();
    [...this.categorySelects, ...this.animalSelects, ...this.topicSelects].forEach((select) => {
      Array.from(select.options).slice(1).forEach((option) => option.remove());
    });
    const categories = new Map();
    const animals = new Map();
    const topics = new Map();

    this.cards.forEach((card) => {
      this.getCardCategories(card).forEach(({ value, label }) => {
        if (!categories.has(value)) categories.set(value, label);
      });

      this.getCardTopics(card).forEach((topic) => {
        const key = this.normalize(topic);

        if (this.animalTagKeys.includes(key)) {
          if (!animals.has(key)) animals.set(key, topic);
          return;
        }

        const current = topics.get(key);
        topics.set(key, {
          label: current?.label || topic,
          count: (current?.count || 0) + 1,
        });
      });
    });

    const sortedCategories = Array.from(categories.entries()).sort((first, second) =>
      first[1].localeCompare(second[1], undefined, { sensitivity: 'base' })
    );

    sortedCategories.forEach(([value, label]) => {
      this.categoryList?.append(this.createCategoryButton(value, label));
      this.categorySelects.forEach((select) => select.append(new Option(label, value)));
    });

    this.animalTagKeys.forEach((value) => {
      const label = animals.get(value);
      if (!label) return;
      this.animalSelects.forEach((select) => select.append(new Option(label, value)));
    });

    const sortedTopics =
      this.topicMode === 'manual'
        ? this.manualTopicKeys
            .filter((value, index, values) => values.indexOf(value) === index && topics.has(value))
            .map((value) => [value, topics.get(value)])
        : Array.from(topics.entries()).sort((first, second) => {
            const countDifference = second[1].count - first[1].count;
            if (countDifference !== 0) return countDifference;
            return first[1].label.localeCompare(second[1].label, undefined, { sensitivity: 'base' });
          });

    sortedTopics.forEach(([value, topic], index) => {
      this.topicSelects.forEach((select) => select.append(new Option(topic.label, value)));
      if (this.topicMode === 'manual' || index < this.topicLimit) {
        this.topicList?.append(this.createTopicButton(value, topic.label));
      }
    });
  }

  createCategoryButton(value, label) {
    const button = document.createElement('button');
    button.className = 'custom-blog-directory__category';
    button.type = 'button';
    button.dataset.categoryFilter = value;
    button.setAttribute('aria-pressed', 'false');

    const mark = document.createElement('span');
    mark.className = 'custom-blog-directory__category-mark';
    mark.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.textContent = label;

    const arrow = document.createElement('span');
    arrow.className = 'custom-blog-directory__category-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '>';

    button.append(mark, text, arrow);
    return button;
  }

  createTopicButton(value, label) {
    const button = document.createElement('button');
    button.className = 'custom-blog-directory__topic';
    button.type = 'button';
    button.dataset.topicFilter = value;
    button.setAttribute('aria-pressed', 'false');
    button.textContent = label;
    return button;
  }

  bindEvents() {
    this.listen(this.categoryList, 'click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-category-filter]');
      if (!(button instanceof HTMLButtonElement)) return;
      this.activeCategory = button.dataset.categoryFilter || '';
      this.resetAndUpdate();
    });

    this.listen(this.topicList, 'click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-topic-filter]');
      if (!(button instanceof HTMLButtonElement)) return;
      const selectedTopic = button.dataset.topicFilter || '';
      this.activeTopic = this.activeTopic === selectedTopic ? '' : selectedTopic;
      this.resetAndUpdate();
    });

    this.categorySelects.forEach((select) => {
      this.listen(select, 'change', () => {
        this.activeCategory = select.value;
        this.resetAndUpdate();
      });
    });

    this.animalSelects.forEach((select) => {
      this.listen(select, 'change', () => {
        this.activeAnimal = select.value;
        this.resetAndUpdate();
      });
    });

    this.topicSelects.forEach((select) => {
      this.listen(select, 'change', () => {
        this.activeTopic = select.value;
        this.resetAndUpdate();
      });
    });

    this.sortSelects.forEach((select) => {
      this.listen(select, 'change', () => {
        this.sortOrder = select.value;
        this.resetAndUpdate();
      });
    });

    this.listen(this.loadMoreButton, 'click', () => {
      this.visibleCount += this.loadCount;
      this.update();
    });
    this.listen(this.retryButton, 'click', () => this.loadAllPages());
  }

  resetAndUpdate() {
    this.visibleCount = this.initialCount;
    this.update();
  }

  update() {
    const matchingCards = this.cards.filter((card) => {
      const categoryValues = this.getCardCategories(card).map((category) => category.value);
      const categoryMatches = !this.activeCategory || categoryValues.includes(this.activeCategory);
      const topicKeys = this.getCardTopics(card).map((topic) => this.normalize(topic));
      const animalMatches = !this.activeAnimal || topicKeys.includes(this.activeAnimal);
      const topicMatches = !this.activeTopic || topicKeys.includes(this.activeTopic);
      return categoryMatches && animalMatches && topicMatches;
    });

    matchingCards.sort((first, second) => {
      if (this.sortOrder === 'oldest') return Number(first.dataset.date) - Number(second.dataset.date);
      if (this.sortOrder === 'title') {
        return (first.dataset.title || '').localeCompare(second.dataset.title || '', undefined, { sensitivity: 'base' });
      }
      return Number(second.dataset.date) - Number(first.dataset.date);
    });

    const matchingSet = new Set(matchingCards);
    matchingCards.forEach((card) => this.grid?.append(card));
    this.cards.filter((card) => !matchingSet.has(card)).forEach((card) => this.grid?.append(card));

    this.cards.forEach((card) => {
      card.hidden = true;
    });
    matchingCards.slice(0, this.visibleCount).forEach((card) => {
      card.hidden = false;
    });

    if (this.count) this.count.textContent = String(matchingCards.length);
    if (this.countContainer) this.countContainer.hidden = !this.ready;
    if (this.emptyState) this.emptyState.hidden = !this.ready || matchingCards.length !== 0;
    if (this.loadMoreButton) this.loadMoreButton.hidden = matchingCards.length <= this.visibleCount;
    if (this.pagination) this.pagination.hidden = this.ready;
    this.querySelectorAll('[data-category-filter], [data-topic-filter], select, [data-load-more]')
      .forEach((control) => { control.disabled = !this.ready; });

    this.syncControls();
  }

  syncControls() {
    this.querySelectorAll('[data-category-filter]').forEach((button) => {
      const isActive = button.dataset.categoryFilter === this.activeCategory;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.querySelectorAll('[data-topic-filter]').forEach((button) => {
      const isActive = button.dataset.topicFilter === this.activeTopic;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.categorySelects.forEach((select) => {
      select.value = this.activeCategory;
    });
    this.topicSelects.forEach((select) => {
      select.value = this.activeTopic;
    });
    this.animalSelects.forEach((select) => {
      select.value = this.activeAnimal;
    });
    this.sortSelects.forEach((select) => {
      select.value = this.sortOrder;
    });
  }
}

if (!customElements.get('custom-blog-directory')) {
  customElements.define('custom-blog-directory', CustomBlogDirectory);
}
